// src/stores/SettingsStore.js - Zustand Store replacing SettingsSocketService + SettingsProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useSettingsStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors SettingsProvider exactly)
    // ================================
    
    // Main settings data - organized by data category
    businessSettings: {},
    businessHours: {},
    services: [],
    serviceComponents: {}, // Keyed by serviceId
    emailTemplates: [],
    smsTemplates: [],
    emailSettings: {},
    notificationPreferences: [],
    userNotificationPreferences: [], // NEW: User-level notification preferences
    emailDomains: [],

    // Loading and error states - granular for different operations
    settingsLoading: false,
    loadingUserNotificationPrefs: false, // NEW: Loading state for user preferences
    savingSettings: false, // General-purpose flag (used by ReferralSettings, etc.)
    savingCompanyInfo: false,
    savingOperationalSettings: false,
    savingHours: false,
    savingServices: false,
    savingBaseService: false,
    savingServiceSettings: false,
    savingComponents: false,
    savingTemplates: false,
    savingEmailSettings: false,
    checkingDomainVerification: false,
    savingNotificationPrefs: false,
    savingProfile: false,

    // Connection state
    isConnected: false,
    
    // Cache state (from SettingsSocketService)
    cache: {
      businessSettings: {},
      businessHours: {},
      services: [],
      serviceComponents: {},
      emailTemplates: [],
      smsTemplates: [],
      smsSettings: {},
      notificationPreferences: [],
      userNotificationPreferences: [], // NEW: Cache for user preferences
      lastFetch: null
    },
    
    // Internal flags
    _initialized: false,
    
    // ================================
    // SOCKET OPERATIONS (from SettingsSocketService)
    // ================================
    
    /**
     * Request all business settings at once - NO RETURN VALUE
     */
    loadAllSettings: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('⚙️ SettingsStore: Loading all settings');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_SETTINGS);
    },
    
    /**
     * Request business hours - NO RETURN VALUE
     */
    loadBusinessHours: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🕒 SettingsStore: Loading business hours');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_BUSINESS_HOURS);
    },
    
    /**
     * Request all business services - NO RETURN VALUE
     */
    loadServices: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🔧 SettingsStore: Loading services');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_SERVICES);
    },
    
    /**
     * Request service components for a specific service - NO RETURN VALUE
     * @param {string} serviceId - Service ID
     */
    loadServiceComponents: (serviceId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🧩 SettingsStore: Loading service components for service:', serviceId);
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_SERVICE_COMPONENTS, {
        service_id: serviceId
      });
    },
    
    /**
     * Request to update one or multiple settings atomically - NO RETURN VALUE
     * @param {object} settings - Object with key-value pairs
     */
    updateSettings: (settings, { savingFlag = 'savingSettings' } = {}) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('⚙️ SettingsStore: Updating settings atomically:', Object.keys(settings));

      set({ [savingFlag]: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_SETTINGS, {
        settings
      });
    },
    
    /**
     * Request to update business hours for all days - NO RETURN VALUE
     * @param {object} hoursData - Complete hours object with numeric keys
     */
    updateBusinessHours: (hoursData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('🕒 SettingsStore: Updating business hours:', hoursData);

      set({ savingHours: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_BUSINESS_HOURS, {
        hours_data: hoursData
      });
    },
    /**
     * Request to create a new business service - NO RETURN VALUE
     * @param {object} serviceData - Service configuration
     */
    createService: (serviceData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      // Validate required fields
      if (!serviceData.name) {
        console.error('❌ SettingsStore: Service name is required');
        return;
      }
      
      console.log('🔧 SettingsStore: Creating service:', serviceData.name);
      
      set({
        savingServices: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_SERVICE, serviceData);
    },
    
    /**
     * Request to update an existing service - NO RETURN VALUE
     * @param {string} serviceId - Service ID
     * @param {object} updateData - Fields to update
     */
    updateService: (serviceId, updateData, { savingFlag = 'savingServices' } = {}) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('🔧 SettingsStore: Updating service:', serviceId);

      set({ [savingFlag]: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_SERVICE, {
        service_id: serviceId,
        ...updateData
      });
    },
    
    /**
     * Request to delete a service - NO RETURN VALUE
     * @param {string} serviceId - Service ID
     */
    deleteService: (serviceId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🔧 SettingsStore: Deleting service:', serviceId);
      
      set({
        savingServices: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_SERVICE, {
        service_id: serviceId
      });
    },
    
    /**
     * Request to create a new service component - NO RETURN VALUE
     * @param {string} serviceId - Service ID
     * @param {object} componentData - Component configuration
     */
    createServiceComponent: (serviceId, componentData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      // Validate required fields
      if (!componentData.name) {
        console.error('❌ SettingsStore: Component name is required');
        return;
      }
      
      console.log('🧩 SettingsStore: Creating service component:', componentData.name, 'for service:', serviceId);
      
      set({
        savingComponents: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_SERVICE_COMPONENT, {
        service_id: serviceId,
        ...componentData
      });
    },
    
    /**
     * Request to update an existing service component - NO RETURN VALUE
     * @param {string} serviceId - Service ID
     * @param {string} componentId - Component ID
     * @param {object} updateData - Fields to update
     */
    updateServiceComponent: (serviceId, componentId, updateData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('🧩 SettingsStore: Updating service component:', componentId, updateData);

      set({ savingComponents: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_SERVICE_COMPONENT, {
        service_id: serviceId,
        component_id: componentId,
        ...updateData
      });
    },
    
    /**
     * Request to delete a service component - NO RETURN VALUE
     * @param {string} serviceId - Service ID
     * @param {string} componentId - Component ID
     */
    deleteServiceComponent: (serviceId, componentId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🧩 SettingsStore: Deleting service component:', componentId);
      
      set({
        savingComponents: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_SERVICE_COMPONENT, {
        service_id: serviceId,
        component_id: componentId
      });
    },
    
    // ================================
    // EMAIL TEMPLATE OPERATIONS (EMIT ONLY)
    // ================================
    
    /**
     * Request all email templates - NO RETURN VALUE
     */
    loadEmailTemplates: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Loading email templates');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EMAIL_TEMPLATES);
    },
    
    /**
     * Request to create a new email template - NO RETURN VALUE
     * @param {object} templateData - Email template data
     */
    createEmailTemplate: (templateData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Creating email template:', templateData.name);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_EMAIL_TEMPLATE, templateData);
    },
    
    /**
     * Request to update an email template - NO RETURN VALUE
     * @param {string} templateId - Template ID
     * @param {object} updateData - Fields to update
     */
    updateEmailTemplate: (templateId, updateData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Updating email template:', templateId, updateData);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_EMAIL_TEMPLATE, {
        template_id: templateId,
        ...updateData
      });
    },
    
    /**
     * Request to delete an email template - NO RETURN VALUE
     * @param {string} templateId - Template ID
     */
    deleteEmailTemplate: (templateId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Deleting email template:', templateId);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_EMAIL_TEMPLATE, {
        template_id: templateId
      });
    },
    
    // ================================
    // SMS TEMPLATE OPERATIONS (EMIT ONLY)
    // ================================
    
    /**
     * Request all SMS templates - NO RETURN VALUE
     */
    loadSMSTemplates: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📱 SettingsStore: Loading SMS templates');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_SMS_TEMPLATES);
    },
    
    /**
     * Request to create a new SMS template - NO RETURN VALUE
     * @param {object} templateData - SMS template data
     */
    createSMSTemplate: (templateData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📱 SettingsStore: Creating SMS template:', templateData.name);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_SMS_TEMPLATE, templateData);
    },
    
    /**
     * Request to update an SMS template - NO RETURN VALUE
     * @param {string} templateId - Template ID
     * @param {object} updateData - Fields to update
     */
    updateSMSTemplate: (templateId, updateData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📱 SettingsStore: Updating SMS template:', templateId, updateData);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_SMS_TEMPLATE, {
        template_id: templateId,
        ...updateData
      });
    },
    
    /**
     * Request to delete an SMS template - NO RETURN VALUE
     * @param {string} templateId - Template ID
     */
    deleteSMSTemplate: (templateId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📱 SettingsStore: Deleting SMS template:', templateId);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_SMS_TEMPLATE, {
        template_id: templateId
      });
    },
    
    /**
     * Request to test a template - NO RETURN VALUE
     * @param {string} templateId - Template ID
     * @param {string} templateType - 'email' or 'sms'
     * @param {object} testData - Test parameters
     */
    testTemplate: (templateId, templateType, testData = {}) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🧪 SettingsStore: Testing template:', templateId, templateType);
      
      set({
        savingTemplates: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.TEST_TEMPLATE, {
        template_id: templateId,
        template_type: templateType,
        ...testData
      });
    },

    // ================================
    // NOTIFICATION PREFERENCE OPERATIONS (EMIT ONLY)
    // ================================
    
    /**
     * Request notification preferences - NO RETURN VALUE
     */
    loadNotificationPreferences: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🔔 SettingsStore: Loading notification preferences');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_NOTIFICATION_PREFERENCES);
    },
    
    /**
     * Request to create default notification preferences - NO RETURN VALUE
     */
    createDefaultNotificationPreferences: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🔔 SettingsStore: Creating default notification preferences');
      
      set({
        savingNotificationPrefs: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_DEFAULT_NOTIFICATION_PREFERENCES);
    },
    
    /**
     * Request to update a notification preference - NO RETURN VALUE
     * @param {string} eventType - Event type
     * @param {string} deliveryMethod - Delivery method
     * @param {boolean} enabled - Whether notification is enabled
     * @param {Array} recipients - Array of email addresses
     */
    updateNotificationPreference: (eventType, deliveryMethod, enabled, recipients = []) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('🔔 SettingsStore: Updating notification preference:', eventType, deliveryMethod, enabled);
      
      set({
        savingNotificationPrefs: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_NOTIFICATION_PREFERENCE, {
        event_type: eventType,
        delivery_method: deliveryMethod,
        enabled,
        settings: {
          recipients
        }
      });
    },

    // ================================
    // USER NOTIFICATION PREFERENCE OPERATIONS (EMIT ONLY) - NEW SYSTEM
    // ================================

    /**
     * Request user notification preferences - NO RETURN VALUE
     */
    loadUserNotificationPreferences: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('🔔 SettingsStore: Loading user notification preferences');

      set({
        loadingUserNotificationPrefs: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_USER_NOTIFICATION_PREFERENCES);
    },

    /**
     * Request to create default user notification preferences - NO RETURN VALUE
     */
    createDefaultUserNotificationPreferences: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('🔔 SettingsStore: Creating default user notification preferences');

      set({
        loadingUserNotificationPrefs: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_DEFAULT_USER_NOTIFICATION_PREFERENCES);
    },

    /**
     * Request to update a user notification preference - NO RETURN VALUE
     * @param {string} eventType - Event type
     * @param {string} deliveryMethod - Delivery method
     * @param {boolean} enabled - Whether notification is enabled
     */
    updateUserNotificationPreference: (eventType, deliveryMethod, enabled) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('🔔 SettingsStore: Updating user notification preference:', eventType, deliveryMethod, enabled);

      set({
        loadingUserNotificationPrefs: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_USER_NOTIFICATION_PREFERENCE, {
        event_type: eventType,
        delivery_method: deliveryMethod,
        enabled
      });
    },

    // ================================
    // USER PROFILE OPERATIONS (EMIT ONLY)
    // ================================

    /**
     * Request to update user profile - NO RETURN VALUE
     * @param {string} name - User's name (optional)
     * @param {string} email - User's email (optional)
     */
    updateUserProfile: (name, email) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('👤 SettingsStore: Updating user profile');

      set({ savingProfile: true });

      const updateData = {};
      if (name !== undefined) updateData.name = name;
      if (email !== undefined) updateData.email = email;

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_USER_PROFILE, updateData);
    },

    // ================================
    // EMAIL SETTINGS OPERATIONS (EMIT ONLY)
    // ================================
    
    /**
     * Get email settings for business
     */
    loadEmailSettings: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Loading email settings');
      
      set({
        settingsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EMAIL_SETTINGS);
    },
    
    /**
     * Update email settings
     * @param {Object} emailSettingsData - Email settings data
     */
    updateEmailSettings: (emailSettingsData) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Updating email settings');
      
      set({
        savingEmailSettings: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_EMAIL_SETTINGS, {
        email_settings: emailSettingsData
      });
    },
    
    /**
     * Setup professional domain for email sending
     * @param {string} domain - Business domain
     * @param {string} sendingEmail - Email address to send from
     */
    setupProfessionalDomain: (domain, sendingEmail) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Setting up professional domain:', domain);
      
      set({
        savingEmailSettings: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.SETUP_PROFESSIONAL_DOMAIN, {
        domain,
        sending_email: sendingEmail
      });
    },
    
    /**
     * Check domain verification status
     */
    checkDomainVerification: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('📧 SettingsStore: Checking domain verification');

      set({
        checkingDomainVerification: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.CHECK_DOMAIN_VERIFICATION);
    },

    /**
     * Verify a specific domain by ID
     * @param {number} domainId - Domain ID to verify
     */
    verifyDomain: (domainId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }

      console.log('📧 SettingsStore: Verifying domain:', domainId);

      set({
        checkingDomainVerification: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.VERIFY_DOMAIN, {
        domain_id: domainId
      });
    },
    
    /**
     * Set active email domain - NO RETURN VALUE
     * @param {string} domainId - Domain ID to make active
     */
    setActiveEmailDomain: (domainId) => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Setting active domain:', domainId);
      
      set({
        savingEmailSettings: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.SET_ACTIVE_EMAIL_DOMAIN, {
        domain_id: domainId
      });
    },
    
    /**
     * Switch to basic email mode - NO RETURN VALUE
     */
    setBasicEmailMode: () => {
      const { socketManager} = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ SettingsStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 SettingsStore: Switching to basic email mode');
      
      set({
        savingEmailSettings: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.SET_BASIC_EMAIL_MODE);
    },
    
    // ================================
    // UTILITY FUNCTIONS (from SettingsProvider)
    // ================================
    
    // Business settings utilities
    getSetting: (key, defaultValue = null) => {
      const { businessSettings } = get();
      return businessSettings[key] ?? defaultValue;
    },
    
    // Single setting update (convenience wrapper)
    updateSetting: (key, value) => {
      get().updateSettings({ [key]: value });
    },
    
    // Business hours utilities
    getBusinessHoursForDay: (dayOfWeek) => {
      const { businessHours } = get();
      return businessHours[dayOfWeek] || null;
    },
    
    isBusinessOpen: () => {
      const { businessHours } = get();
      const now = new Date();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
      
      const dayHours = businessHours[currentDay];
      
      if (!dayHours || !dayHours.is_open) {
        return false;
      }
      
      return currentTime >= dayHours.open_time && currentTime <= dayHours.close_time;
    },
    
    // Services utilities
    getActiveServices: () => {
      const { services } = get();
      return services.filter(service => service.active !== false);
    },
    
    getServiceById: (serviceId) => {
      const { services } = get();
      return services.find(s => s.id === serviceId) || null;
    },
    
    getServiceComponents: (serviceId) => {
      const { serviceComponents } = get();
      return serviceComponents[serviceId] || [];
    },
    
    getServiceComponentById: (serviceId, componentId) => {
      const components = get().getServiceComponents(serviceId);
      return components.find(c => c.id === componentId) || null;
    },
    
    getComponentsByType: (serviceId, componentType) => {
      const components = get().getServiceComponents(serviceId);
      return components.filter(c => c.component_type === componentType);
    },
    
    // Template utilities
    getEmailTemplateById: (templateId) => {
      const { emailTemplates } = get();
      return emailTemplates.find(template => template.id === templateId) || null;
    },
    
    getSMSTemplateById: (templateId) => {
      const { smsTemplates } = get();
      return smsTemplates.find(template => template.id === templateId) || null;
    },
    
    // Email utilities
    getEmailSetting: (key, defaultValue = null) => {
      const { emailSettings } = get();
      return emailSettings[key] ?? defaultValue;
    },
    
    isEmailEnabled: () => {
      const { emailSettings } = get();
      return emailSettings.enabled === true;
    },
    
    isEmailConfigured: () => {
      const { emailSettings } = get();
      return emailSettings.from_name && emailSettings.reply_to_email;
    },
    
    isProfessionalSetup: () => {
      const { emailSettings } = get();
      return !!(emailSettings.custom_domain && emailSettings.sendgrid_domain_id);
    },
    
    isDomainVerified: () => {
      const { emailSettings } = get();
      return emailSettings.is_domain_verified === true;
    },
    
    getEmailDomains: () => {
      const { emailDomains } = get();
      return emailDomains;
    },
    
    getVerifiedEmailDomains: () => {
      const { emailDomains } = get();
      return emailDomains.filter(domain => domain.is_verified);
    },
    
    getActiveEmailDomain: () => {
      const { emailSettings, emailDomains } = get();
      const activeDomainId = emailSettings.active_domain_id;
      if (!activeDomainId) return null;
      return emailDomains.find(domain => domain.id === activeDomainId) || null;
    },
    
    // Notification preference utilities
    getNotificationPreference: (eventType, deliveryMethod) => {
      const { notificationPreferences } = get();
      return notificationPreferences.find(
        pref => pref.event_type === eventType && pref.delivery_method === deliveryMethod
      ) || null;
    },
    
    isNotificationEnabled: (eventType, deliveryMethod) => {
      const pref = get().getNotificationPreference(eventType, deliveryMethod);
      return pref ? pref.enabled : false;
    },
    
    getNotificationRecipients: (eventType, deliveryMethod) => {
      const pref = get().getNotificationPreference(eventType, deliveryMethod);
      return pref?.settings?.recipients || [];
    },

    // User notification preference utilities (NEW SYSTEM)
    getUserNotificationPreference: (eventType, deliveryMethod) => {
      const { userNotificationPreferences } = get();
      return userNotificationPreferences.find(
        pref => pref.event_type === eventType && pref.delivery_method === deliveryMethod
      ) || null;
    },

    isUserNotificationEnabled: (eventType, deliveryMethod) => {
      const pref = get().getUserNotificationPreference(eventType, deliveryMethod);
      return pref ? pref.enabled : false;
    },

    // General utilities
    clearSettingsError: () => {
    },
    
    refreshSettings: () => {
      // Clear cache first
      set(state => ({
        cache: {
          businessSettings: {},
          businessHours: {},
          services: [],
          serviceComponents: {},
          emailTemplates: [],
          smsTemplates: [],
          smsSettings: {},
          notificationPreferences: [],
          lastFetch: null
        }
      }));
      
      // Then reload
      get().loadAllSettings();
    },
    
    // Cache utilities
    getCachedData: () => {
      const { cache } = get();
      return {
        businessSettings: { ...cache.businessSettings },
        businessHours: { ...cache.businessHours },
        services: [...cache.services],
        serviceComponents: { ...cache.serviceComponents },
        emailTemplates: [...cache.emailTemplates],
        smsSettings: { ...cache.smsSettings },
        lastFetch: cache.lastFetch
      };
    },
    
    isDataFresh: () => {
      const { cache } = get();
      if (!cache.lastFetch) return false;
      
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
      return cache.lastFetch > tenMinutesAgo;
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setSettingsLoaded: (data) => {
      console.log('📡 SettingsStore: Complete settings update received');

      // Extract components from services into serviceComponents object
      const serviceComponents = {};
      if (data.services) {
        data.services.forEach(service => {
          if (service.components) {
            serviceComponents[service.id] = service.components;
            console.log(`📡 SettingsStore: Loaded ${service.components.length} components for service ${service.name}`);
          }
        });
      }

      set(state => ({
        businessSettings: data.business_settings || {},
        businessHours: data.business_hours || {},
        services: data.services || [],
        serviceComponents: serviceComponents,
        emailSettings: data.email_settings || {},
        emailDomains: data.email_domains || [],
        settingsLoading: false,
        savingSettings: false,
        savingCompanyInfo: false,
        savingOperationalSettings: false,
        savingBaseService: false,
        savingServiceSettings: false,
        cache: {
          ...state.cache,
          businessSettings: data.business_settings || {},
          businessHours: data.business_hours || {},
          services: data.services || [],
          serviceComponents: serviceComponents,
          lastFetch: new Date()
        }
      }));
    },
    
    _setBusinessHoursLoaded: (data) => {
      console.log('📡 SettingsStore: Business hours loaded');
      
      set(state => ({
        businessHours: data,
        settingsLoading: false,
        savingHours: false,
        cache: {
          ...state.cache,
          businessHours: data
        }
      }));
    },
    
    _setServicesLoaded: (data) => {
      console.log('📡 SettingsStore: Services loaded:', data.length, 'services');
      
      set(state => ({
        services: data,
        settingsLoading: false,
        savingServices: false,
        cache: {
          ...state.cache,
          services: data
        }
      }));
    },
    
    _setServiceComponentsLoaded: (data) => {
      console.log('📡 SettingsStore: Service components loaded:', data.components.length, 'components for service:', data.service_id);
      
      set(state => ({
        serviceComponents: {
          ...state.serviceComponents,
          [data.service_id]: data.components
        },
        savingComponents: false,
        cache: {
          ...state.cache,
          serviceComponents: {
            ...state.cache.serviceComponents,
            [data.service_id]: data.components
          }
        }
      }));
    },
    
    _updateBusinessSettings: (data) => {
      console.log('📡 SettingsStore: Batch settings update received:', data.count, 'settings');

      set(state => {
        const updatedSettings = data.settings ? { ...state.businessSettings, ...data.settings } : state.businessSettings;

        return {
          businessSettings: updatedSettings,
          savingSettings: false,
          savingCompanyInfo: false,
          savingOperationalSettings: false,
          cache: {
            ...state.cache,
            businessSettings: updatedSettings
          }
        };
      });
    },
    
    _updateBusinessHours: (data) => {
      console.log('📡 SettingsStore: Business hours updated');
      
      set(state => {
        // Replace entire hours state with all_hours from backend response
        const updatedHours = data.all_hours || state.businessHours;
        
        return {
          businessHours: updatedHours,
          savingHours: false,
          cache: {
            ...state.cache,
            businessHours: updatedHours
          }
        };
      });
    },
    
    _addService: (data) => {
      console.log('📡 SettingsStore: Service created:', data.service.name);
      
      set(state => {
        const updatedServices = [...state.services, data.service];
        
        return {
          services: updatedServices,
          savingServices: false,
          cache: {
            ...state.cache,
            services: updatedServices
          }
        };
      });
    },
    
    _updateService: (data) => {
      console.log('📡 SettingsStore: Service updated:', data.service.id);

      set(state => {
        const updatedServices = state.services.map(service =>
          service.id === data.service.id ? data.service : service
        );

        return {
          services: updatedServices,
          savingServices: false,
          savingBaseService: false,
          savingServiceSettings: false,
          cache: {
            ...state.cache,
            services: updatedServices
          }
        };
      });
    },
    
    _removeService: (data) => {
      console.log('📡 SettingsStore: Service deleted:', data.service_id);
      
      set(state => {
        const updatedServices = state.services.filter(service => service.id !== data.service_id);
        const updatedComponents = { ...state.serviceComponents };
        delete updatedComponents[data.service_id];
        
        return {
          services: updatedServices,
          serviceComponents: updatedComponents,
          savingServices: false,
          cache: {
            ...state.cache,
            services: updatedServices,
            serviceComponents: { ...state.cache.serviceComponents }
          }
        };
      });
    },
    
    _addServiceComponent: (data) => {
      console.log('📡 SettingsStore: Service component created:', data.component.name, 'for service:', data.service_id);
      
      set(state => ({
        serviceComponents: {
          ...state.serviceComponents,
          [data.service_id]: [
            ...(state.serviceComponents[data.service_id] || []),
            data.component
          ]
        },
        savingComponents: false
      }));
    },
    
    _updateServiceComponent: (data) => {
      console.log('📡 SettingsStore: Service component updated:', data.component.name, 'for service:', data.service_id);
      
      set(state => ({
        serviceComponents: {
          ...state.serviceComponents,
          [data.service_id]: (state.serviceComponents[data.service_id] || []).map(component =>
            component.id === data.component.id ? data.component : component
          )
        },
        savingComponents: false
      }));
    },
    
    _removeServiceComponent: (data) => {
      console.log('📡 SettingsStore: Service component deleted:', data.component_id, 'from service:', data.service_id);
      
      set(state => ({
        serviceComponents: {
          ...state.serviceComponents,
          [data.service_id]: (state.serviceComponents[data.service_id] || []).filter(component => 
            component.id !== data.component_id
          )
        },
        savingComponents: false
      }));
    },
    
    // Email template updaters
    _setEmailTemplatesLoaded: (data) => {
      console.log('📡 SettingsStore: Email templates loaded:', data.length, 'templates');
      
      set(state => ({
        emailTemplates: data,
        settingsLoading: false,
        savingTemplates: false,
        cache: {
          ...state.cache,
          emailTemplates: data
        }
      }));
    },
    
    _addEmailTemplate: (data) => {
      console.log('📡 SettingsStore: Email template created:', data.name);
      
      set(state => {
        const updatedTemplates = [...state.emailTemplates, data];
        
        return {
          emailTemplates: updatedTemplates,
          savingTemplates: false,
          cache: {
            ...state.cache,
            emailTemplates: updatedTemplates
          }
        };
      });
    },
    
    _updateEmailTemplate: (data) => {
      // Handle both array (new format) and single object (backward compatibility)
      const templates = Array.isArray(data) ? data : [data];
      
      console.log('📡 SettingsStore: Email template updated:', templates.length, 'template(s)');
      
      set(state => {
        let updatedTemplates = [...state.emailTemplates];
        templates.forEach(template => {
          const index = updatedTemplates.findIndex(t => t.id === template.id);
          if (index >= 0) {
            updatedTemplates[index] = template;
          }
        });
        
        return {
          emailTemplates: updatedTemplates,
          savingTemplates: false,
          cache: {
            ...state.cache,
            emailTemplates: updatedTemplates
          }
        };
      });
    },
    
    _removeEmailTemplate: (data) => {
      console.log('📡 SettingsStore: Email template deleted:', data.template_id);
      
      set(state => {
        const updatedTemplates = state.emailTemplates.filter(template => template.id !== data.template_id);
        
        return {
          emailTemplates: updatedTemplates,
          savingTemplates: false,
          cache: {
            ...state.cache,
            emailTemplates: updatedTemplates
          }
        };
      });
    },
    
    // SMS template updaters
    _setSMSTemplatesLoaded: (data) => {
      console.log('📡 SettingsStore: SMS templates loaded:', data.length, 'templates');
      
      set(state => ({
        smsTemplates: data,
        settingsLoading: false,
        savingTemplates: false,
        cache: {
          ...state.cache,
          smsTemplates: data
        }
      }));
    },
    
    _addSMSTemplate: (data) => {
      console.log('📡 SettingsStore: SMS template created:', data.name);
      
      set(state => {
        const updatedTemplates = [...state.smsTemplates, data];
        
        return {
          smsTemplates: updatedTemplates,
          savingTemplates: false,
          cache: {
            ...state.cache,
            smsTemplates: updatedTemplates
          }
        };
      });
    },
    
    _updateSMSTemplate: (data) => {
      // Handle both array (new format) and single object (backward compatibility)
      const templates = Array.isArray(data) ? data : [data];
      
      console.log('📡 SettingsStore: SMS template updated:', templates.length, 'template(s)');
      
      set(state => {
        let updatedTemplates = [...state.smsTemplates];
        templates.forEach(template => {
          const index = updatedTemplates.findIndex(t => t.id === template.id);
          if (index >= 0) {
            updatedTemplates[index] = template;
          }
        });
        
        return {
          smsTemplates: updatedTemplates,
          savingTemplates: false,
          cache: {
            ...state.cache,
            smsTemplates: updatedTemplates
          }
        };
      });
    },
    
    _removeSMSTemplate: (data) => {
      console.log('📡 SettingsStore: SMS template deleted:', data.template_id);
      
      set(state => {
        const updatedTemplates = state.smsTemplates.filter(template => template.id !== data.template_id);
        
        return {
          smsTemplates: updatedTemplates,
          savingTemplates: false,
          cache: {
            ...state.cache,
            smsTemplates: updatedTemplates
          }
        };
      });
    },
    
    _setTemplateTested: (data) => {
      console.log('📡 SettingsStore: Template test completed:', data);
      
      set({
        savingTemplates: false
        // Could add success message state here if needed
      });
    },
    
    // Notification preference updaters
    _setNotificationPreferencesLoaded: (data) => {
      console.log('📡 SettingsStore: Notification preferences loaded:', data.length, 'preferences');
      
      set(state => ({
        notificationPreferences: data,
        settingsLoading: false,
        savingNotificationPrefs: false,
        cache: {
          ...state.cache,
          notificationPreferences: data
        }
      }));
    },
    
    _updateNotificationPreference: (data) => {
      console.log('📡 SettingsStore: Notification preference updated:', data.event_type, data.delivery_method);
      
      set(state => {
        const index = state.notificationPreferences.findIndex(
          pref => pref.event_type === data.event_type && pref.delivery_method === data.delivery_method
        );
        
        let updatedPreferences;
        if (index >= 0) {
          // Update existing preference
          updatedPreferences = [...state.notificationPreferences];
          updatedPreferences[index] = data;
        } else {
          // Add new preference
          updatedPreferences = [...state.notificationPreferences, data];
        }
        
        return {
          notificationPreferences: updatedPreferences,
          savingNotificationPrefs: false,
          cache: {
            ...state.cache,
            notificationPreferences: updatedPreferences
          }
        };
      });
    },
    
    _addDefaultNotificationPreferences: (data) => {
      console.log('📡 SettingsStore: Default notification preferences created:', data.length, 'preferences');

      set(state => {
        const updatedPreferences = [...state.notificationPreferences, ...data];

        return {
          notificationPreferences: updatedPreferences,
          savingNotificationPrefs: false,
          cache: {
            ...state.cache,
            notificationPreferences: updatedPreferences
          }
        };
      });
    },

    // User notification preference updaters (NEW SYSTEM)
    _setUserNotificationPreferencesLoaded: (data) => {
      console.log('📡 SettingsStore: User notification preferences loaded:', data.length, 'preferences');

      set({
        userNotificationPreferences: data,
        loadingUserNotificationPrefs: false
      });
    },

    _updateUserNotificationPreference: (data) => {
      console.log('📡 SettingsStore: User notification preference updated:', data.event_type, data.delivery_method);

      set(state => {
        const index = state.userNotificationPreferences.findIndex(
          pref => pref.event_type === data.event_type && pref.delivery_method === data.delivery_method
        );

        let updatedPreferences;
        if (index >= 0) {
          // Update existing preference
          updatedPreferences = [...state.userNotificationPreferences];
          updatedPreferences[index] = data;
        } else {
          // Add new preference
          updatedPreferences = [...state.userNotificationPreferences, data];
        }

        return {
          userNotificationPreferences: updatedPreferences,
          loadingUserNotificationPrefs: false
        };
      });
    },

    _addDefaultUserNotificationPreferences: (data) => {
      console.log('📡 SettingsStore: Default user notification preferences created:', data.length, 'preferences');

      set(state => ({
        userNotificationPreferences: data,
        loadingUserNotificationPrefs: false
      }));
    },

    // Email settings updaters
    _setEmailSettingsLoaded: (data) => {
      console.log('📡 SettingsStore: Email settings loaded');
      
      set({
        emailSettings: data.email_settings || {},
        settingsLoading: false,
        savingEmailSettings: false
      });
    },
    
    _updateEmailSettings: (data) => {
      console.log('📡 SettingsStore: Email settings updated');
      
      set({
        emailSettings: data.email_settings || {},
        savingEmailSettings: false
      });
    },
    
    _setProfessionalDomainSetup: (data) => {
      console.log('📡 SettingsStore: Professional domain setup completed', data);

      set({
        emailSettings: data.email_settings || {},
        emailDomains: data.email_domains || get().emailDomains,
        savingEmailSettings: false
      });
    },

    _setDomainVerificationStatus: (data) => {
      console.log('📡 SettingsStore: Domain verification status received', data);

      set({
        emailDomains: data.email_domains || get().emailDomains,
        checkingDomainVerification: false
      });
    },

    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================
    
    _getSocketManager: () => {
      // Access socket manager from window global
      return { 
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null 
      };
    },
    
    _initialize: (socketManager) => {
      console.log('🔌 SettingsStore: Initializing with socket manager');
      
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }
      
      set({ 
        isConnected: true,
        _initialized: true 
      });
      
      // Always auto-load settings on initialization
      console.log('🔄 SettingsStore: Auto-loading settings (first time)');

      // Load all settings data
      get().loadAllSettings();
      get().loadNotificationPreferences();
      get().loadUserNotificationPreferences(); // NEW: Load user preferences
      get().loadBusinessHours();
      get().loadServices();
      get().loadEmailTemplates();
      get().loadSMSTemplates();
    },
    
    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },
    
    _handleAuthReset: () => {
      console.log('🧹 SettingsStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        businessSettings: {},
        businessHours: {},
        services: [],
        serviceComponents: {},
        emailTemplates: [],
        smsTemplates: [],
        emailSettings: {},
        notificationPreferences: [],
        userNotificationPreferences: [], // NEW: Reset user preferences
        emailDomains: [],
        settingsLoading: false,
        loadingUserNotificationPrefs: false, // NEW: Reset loading state
        savingSettings: false,
        savingCompanyInfo: false,
        savingOperationalSettings: false,
        savingHours: false,
        savingServices: false,
        savingBaseService: false,
        savingServiceSettings: false,
        savingComponents: false,
        savingTemplates: false,
        savingEmailSettings: false,
        checkingDomainVerification: false,
        savingNotificationPrefs: false,
        savingProfile: false,
        isConnected: false,
        cache: {
          businessSettings: {},
          businessHours: {},
          services: [],
          serviceComponents: {},
          emailTemplates: [],
          smsTemplates: [],
          smsSettings: {},
          notificationPreferences: [],
          userNotificationPreferences: [], // NEW: Reset cache
          lastFetch: null
        },
        _initialized: false
      });
    },

    _handleDisconnect: () => {
      console.log('🔌 SettingsStore: Clearing saving flags due to socket disconnect');

      set({
        savingSettings: false,
        savingCompanyInfo: false,
        savingOperationalSettings: false,
        savingHours: false,
        savingServices: false,
        savingBaseService: false,
        savingServiceSettings: false,
        savingComponents: false,
        savingTemplates: false,
        savingEmailSettings: false,
        checkingDomainVerification: false,
        savingNotificationPrefs: false,
        savingProfile: false,
        loadingUserNotificationPrefs: false,
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================

export const initializeSettingsStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 SettingsStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all settings events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useSettingsStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.SETTINGS_LOADED:
        store._setSettingsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.BUSINESS_HOURS_LOADED:
        store._setBusinessHoursLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICES_LOADED:
        store._setServicesLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICE_COMPONENTS_LOADED:
        store._setServiceComponentsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.SETTINGS_UPDATED:
        store._updateBusinessSettings(responseData.data);
        break;
        
      case SOCKET_EVENTS.BUSINESS_HOURS_UPDATED:
        store._updateBusinessHours(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICE_CREATED:
        store._addService(responseData.data);
        break;

      case SOCKET_EVENTS.SERVICE_UPDATED:
        store._updateService(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICE_DELETED:
        store._removeService(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICE_COMPONENT_CREATED:
        store._addServiceComponent(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICE_COMPONENT_UPDATED:
        store._updateServiceComponent(responseData.data);
        break;
        
      case SOCKET_EVENTS.SERVICE_COMPONENT_DELETED:
        store._removeServiceComponent(responseData.data);
        break;
        
      // Email template events
      case SOCKET_EVENTS.EMAIL_TEMPLATES_LOADED:
        store._setEmailTemplatesLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.EMAIL_TEMPLATE_CREATED:
        store._addEmailTemplate(responseData.data);
        break;
        
      case SOCKET_EVENTS.EMAIL_TEMPLATE_UPDATED:
        store._updateEmailTemplate(responseData.data);
        break;
        
      case SOCKET_EVENTS.EMAIL_TEMPLATE_DELETED:
        store._removeEmailTemplate(responseData.data);
        break;
        
      // SMS template events
      case SOCKET_EVENTS.SMS_TEMPLATES_LOADED:
        store._setSMSTemplatesLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.SMS_TEMPLATE_CREATED:
        store._addSMSTemplate(responseData.data);
        break;
        
      case SOCKET_EVENTS.SMS_TEMPLATE_UPDATED:
        store._updateSMSTemplate(responseData.data);
        break;
        
      case SOCKET_EVENTS.SMS_TEMPLATE_DELETED:
        store._removeSMSTemplate(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEMPLATE_TESTED:
        store._setTemplateTested(responseData.data);
        break;
        
      // Notification preference events
      case SOCKET_EVENTS.NOTIFICATION_PREFERENCES_LOADED:
        store._setNotificationPreferencesLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.NOTIFICATION_PREFERENCE_UPDATED:
        store._updateNotificationPreference(responseData.data);
        break;
        
      case SOCKET_EVENTS.DEFAULT_NOTIFICATION_PREFERENCES_CREATED:
        store._addDefaultNotificationPreferences(responseData.data);
        break;

      // User notification preference events (NEW SYSTEM)
      case SOCKET_EVENTS.USER_NOTIFICATION_PREFERENCES_LOADED:
        store._setUserNotificationPreferencesLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.USER_NOTIFICATION_PREFERENCE_UPDATED:
        store._updateUserNotificationPreference(responseData.data);
        break;

      case SOCKET_EVENTS.DEFAULT_USER_NOTIFICATION_PREFERENCES_CREATED:
        store._addDefaultUserNotificationPreferences(responseData.data);
        break;

      // User profile events
      case SOCKET_EVENTS.USER_PROFILE_UPDATED:
        useSettingsStore.setState({ savingProfile: false });
        console.log('📡 SettingsStore: User profile updated successfully');
        break;

      // Email settings events
      case SOCKET_EVENTS.EMAIL_SETTINGS_LOADED:
        store._setEmailSettingsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.EMAIL_SETTINGS_UPDATED:
        store._updateEmailSettings(responseData.data);
        break;
        
      case SOCKET_EVENTS.PROFESSIONAL_DOMAIN_SETUP:
        store._setProfessionalDomainSetup(responseData.data);
        break;
        
      case SOCKET_EVENTS.DOMAIN_VERIFICATION_STATUS:
        store._setDomainVerificationStatus(responseData.data);
        break;

      case SOCKET_EVENTS.DOMAIN_VERIFIED:
        store._setDomainVerificationStatus(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_SETTINGS_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.UPDATE_SETTINGS_FAILED:
        useSettingsStore.setState({ savingSettings: false, savingCompanyInfo: false, savingOperationalSettings: false });
        break;

      case SOCKET_EVENTS.GET_BUSINESS_HOURS_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.UPDATE_BUSINESS_HOURS_FAILED:
        useSettingsStore.setState({ savingHours: false });
        break;

      case SOCKET_EVENTS.GET_SERVICES_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_SERVICE_FAILED:
      case SOCKET_EVENTS.UPDATE_SERVICE_FAILED:
      case SOCKET_EVENTS.DELETE_SERVICE_FAILED:
        useSettingsStore.setState({ savingServices: false, savingBaseService: false, savingServiceSettings: false });
        break;

      case SOCKET_EVENTS.GET_SERVICE_COMPONENTS_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_SERVICE_COMPONENT_FAILED:
      case SOCKET_EVENTS.UPDATE_SERVICE_COMPONENT_FAILED:
      case SOCKET_EVENTS.DELETE_SERVICE_COMPONENT_FAILED:
        useSettingsStore.setState({ savingComponents: false });
        break;

      case SOCKET_EVENTS.GET_EMAIL_TEMPLATES_FAILED:
      case SOCKET_EVENTS.GET_SMS_TEMPLATES_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_EMAIL_TEMPLATE_FAILED:
      case SOCKET_EVENTS.UPDATE_EMAIL_TEMPLATE_FAILED:
      case SOCKET_EVENTS.DELETE_EMAIL_TEMPLATE_FAILED:
      case SOCKET_EVENTS.CREATE_SMS_TEMPLATE_FAILED:
      case SOCKET_EVENTS.UPDATE_SMS_TEMPLATE_FAILED:
      case SOCKET_EVENTS.DELETE_SMS_TEMPLATE_FAILED:
      case SOCKET_EVENTS.TEST_TEMPLATE_FAILED:
        useSettingsStore.setState({ savingTemplates: false });
        break;

      case SOCKET_EVENTS.GET_NOTIFICATION_PREFERENCES_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.UPDATE_NOTIFICATION_PREFERENCE_FAILED:
      case SOCKET_EVENTS.CREATE_DEFAULT_NOTIFICATION_PREFERENCES_FAILED:
        useSettingsStore.setState({ savingNotificationPrefs: false });
        break;

      // User notification preference error events (NEW SYSTEM)
      case SOCKET_EVENTS.GET_USER_NOTIFICATION_PREFERENCES_FAILED:
      case SOCKET_EVENTS.UPDATE_USER_NOTIFICATION_PREFERENCE_FAILED:
      case SOCKET_EVENTS.CREATE_DEFAULT_USER_NOTIFICATION_PREFERENCES_FAILED:
        useSettingsStore.setState({ loadingUserNotificationPrefs: false });
        break;

      case SOCKET_EVENTS.UPDATE_USER_PROFILE_FAILED:
        useSettingsStore.setState({ savingProfile: false });
        break;

      case SOCKET_EVENTS.GET_EMAIL_SETTINGS_FAILED:
        useSettingsStore.setState({ settingsLoading: false });
        break;

      case SOCKET_EVENTS.UPDATE_EMAIL_SETTINGS_FAILED:
      case SOCKET_EVENTS.SETUP_PROFESSIONAL_DOMAIN_FAILED:
        useSettingsStore.setState({ savingEmailSettings: false });
        break;

      case SOCKET_EVENTS.CHECK_DOMAIN_VERIFICATION_FAILED:
        useSettingsStore.setState({ checkingDomainVerification: false });
        console.log('❌ SettingsStore: Operation failed -', responseData.message);
        break;
        
      default:
        // Not a settings-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners using existing system
  const settingsEvents = [
    // Success events
    SOCKET_EVENTS.SETTINGS_LOADED,
    SOCKET_EVENTS.BUSINESS_HOURS_LOADED,
    SOCKET_EVENTS.SERVICES_LOADED,
    SOCKET_EVENTS.SERVICE_COMPONENTS_LOADED,
    SOCKET_EVENTS.SETTINGS_UPDATED,
    SOCKET_EVENTS.BUSINESS_HOURS_UPDATED,
    SOCKET_EVENTS.SERVICE_CREATED,
    SOCKET_EVENTS.SERVICE_UPDATED,
    SOCKET_EVENTS.SERVICE_DELETED,
    SOCKET_EVENTS.SERVICE_COMPONENT_CREATED,
    SOCKET_EVENTS.SERVICE_COMPONENT_UPDATED,
    SOCKET_EVENTS.SERVICE_COMPONENT_DELETED,
    // Template events
    SOCKET_EVENTS.EMAIL_TEMPLATES_LOADED,
    SOCKET_EVENTS.EMAIL_TEMPLATE_CREATED,
    SOCKET_EVENTS.EMAIL_TEMPLATE_UPDATED,
    SOCKET_EVENTS.EMAIL_TEMPLATE_DELETED,
    SOCKET_EVENTS.SMS_TEMPLATES_LOADED,
    SOCKET_EVENTS.SMS_TEMPLATE_CREATED,
    SOCKET_EVENTS.SMS_TEMPLATE_UPDATED,
    SOCKET_EVENTS.SMS_TEMPLATE_DELETED,
    SOCKET_EVENTS.TEMPLATE_TESTED,
    // Notification events
    SOCKET_EVENTS.NOTIFICATION_PREFERENCES_LOADED,
    SOCKET_EVENTS.NOTIFICATION_PREFERENCE_UPDATED,
    SOCKET_EVENTS.DEFAULT_NOTIFICATION_PREFERENCES_CREATED,
    // User notification events (NEW SYSTEM)
    SOCKET_EVENTS.USER_NOTIFICATION_PREFERENCES_LOADED,
    SOCKET_EVENTS.USER_NOTIFICATION_PREFERENCE_UPDATED,
    SOCKET_EVENTS.DEFAULT_USER_NOTIFICATION_PREFERENCES_CREATED,
    // User profile events
    SOCKET_EVENTS.USER_PROFILE_UPDATED,
    // Email settings events
    SOCKET_EVENTS.EMAIL_SETTINGS_LOADED,
    SOCKET_EVENTS.EMAIL_SETTINGS_UPDATED,
    SOCKET_EVENTS.PROFESSIONAL_DOMAIN_SETUP,
    SOCKET_EVENTS.DOMAIN_VERIFICATION_STATUS,
    SOCKET_EVENTS.DOMAIN_VERIFIED,
    // Error events
    SOCKET_EVENTS.GET_SETTINGS_FAILED,
    SOCKET_EVENTS.UPDATE_SETTINGS_FAILED,
    SOCKET_EVENTS.GET_BUSINESS_HOURS_FAILED,
    SOCKET_EVENTS.UPDATE_BUSINESS_HOURS_FAILED,
    SOCKET_EVENTS.GET_SERVICES_FAILED,
    SOCKET_EVENTS.CREATE_SERVICE_FAILED,
    SOCKET_EVENTS.UPDATE_SERVICE_FAILED,
    SOCKET_EVENTS.DELETE_SERVICE_FAILED,
    SOCKET_EVENTS.GET_SERVICE_COMPONENTS_FAILED,
    SOCKET_EVENTS.CREATE_SERVICE_COMPONENT_FAILED,
    SOCKET_EVENTS.UPDATE_SERVICE_COMPONENT_FAILED,
    SOCKET_EVENTS.DELETE_SERVICE_COMPONENT_FAILED,
    SOCKET_EVENTS.GET_EMAIL_TEMPLATES_FAILED,
    SOCKET_EVENTS.CREATE_EMAIL_TEMPLATE_FAILED,
    SOCKET_EVENTS.UPDATE_EMAIL_TEMPLATE_FAILED,
    SOCKET_EVENTS.DELETE_EMAIL_TEMPLATE_FAILED,
    SOCKET_EVENTS.GET_SMS_TEMPLATES_FAILED,
    SOCKET_EVENTS.CREATE_SMS_TEMPLATE_FAILED,
    SOCKET_EVENTS.UPDATE_SMS_TEMPLATE_FAILED,
    SOCKET_EVENTS.DELETE_SMS_TEMPLATE_FAILED,
    SOCKET_EVENTS.TEST_TEMPLATE_FAILED,
    SOCKET_EVENTS.GET_NOTIFICATION_PREFERENCES_FAILED,
    SOCKET_EVENTS.UPDATE_NOTIFICATION_PREFERENCE_FAILED,
    SOCKET_EVENTS.CREATE_DEFAULT_NOTIFICATION_PREFERENCES_FAILED,
    // User notification error events (NEW SYSTEM)
    SOCKET_EVENTS.GET_USER_NOTIFICATION_PREFERENCES_FAILED,
    SOCKET_EVENTS.UPDATE_USER_NOTIFICATION_PREFERENCE_FAILED,
    SOCKET_EVENTS.CREATE_DEFAULT_USER_NOTIFICATION_PREFERENCES_FAILED,
    SOCKET_EVENTS.UPDATE_USER_PROFILE_FAILED,
    SOCKET_EVENTS.GET_EMAIL_SETTINGS_FAILED,
    SOCKET_EVENTS.UPDATE_EMAIL_SETTINGS_FAILED,
    SOCKET_EVENTS.SETUP_PROFESSIONAL_DOMAIN_FAILED,
    SOCKET_EVENTS.CHECK_DOMAIN_VERIFICATION_FAILED
  ];
  
  // Set up individual event listeners using existing system
  settingsEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset and disconnect listeners
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useSettingsStore.getState()._handleAuthReset();
    });
    window.addEventListener('socket-disconnected', () => {
      useSettingsStore.getState()._handleDisconnect();
    });
  }
  
  // Initialize the store
  useSettingsStore.getState()._initialize(socketManager);
  
  console.log('🔌 SettingsStore: Socket event listeners initialized');
};

export default useSettingsStore;